"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GenericDistroSDKProvider = void 0;
/* --------------------------------------------------------------------------------------------
 *  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ------------------------------------------------------------------------------------------ */
const path = require("path");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const CacheTimeConstants_1 = require("./CacheTimeConstants");
const IDistroDotnetSDKProvider_1 = require("./IDistroDotnetSDKProvider");
const versionUtils = require("./VersionUtilities");
class GenericDistroSDKProvider extends IDistroDotnetSDKProvider_1.IDistroDotnetSDKProvider {
    constructor() {
        super(...arguments);
        this.resolvePathAsSymlink = true;
    }
    installDotnet(fullySpecifiedVersion, installType) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.injectPMCFeed(fullySpecifiedVersion, installType);
            let commands = this.myDistroCommands(this.installCommandKey);
            const sdkPackage = yield this.myDotnetVersionPackageName(fullySpecifiedVersion, installType);
            commands = CommandExecutor_1.CommandExecutor.replaceSubstringsInCommands(commands, this.missingPackageNameKey, sdkPackage);
            const updateCommandsResult = (yield this.commandRunner.executeMultipleCommands(commands.slice(0, -1), null, false))[0];
            const installCommandResult = (yield this.commandRunner.execute(commands.slice(-1)[0], null, false)).status;
            return installCommandResult;
        });
    }
    getInstalledGlobalDotnetPathIfExists(installType) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const commandResult = yield this.commandRunner.executeMultipleCommands(this.myDistroCommands(this.currentInstallPathCommandKey), null, false);
            if (commandResult[0].status !== '0') // no dotnet error can be returned, do not want to try to parse this as a path
             {
                return null;
            }
            if (commandResult[0].stdout) {
                commandResult[0].stdout = commandResult[0].stdout.trim();
            }
            if (commandResult[0] && this.resolvePathAsSymlink) {
                let symLinkReadCommand = this.myDistroCommands(this.readSymbolicLinkCommandKey);
                symLinkReadCommand = CommandExecutor_1.CommandExecutor.replaceSubstringsInCommands(symLinkReadCommand, this.missingPathKey, commandResult[0].stdout);
                const resolvedPath = (yield this.commandRunner.executeMultipleCommands(symLinkReadCommand, { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.READ_SYMLINK_CACHE_DURATION_MS }, false))[0].stdout;
                if (resolvedPath) {
                    return path.dirname(resolvedPath.trim());
                }
            }
            return (_a = commandResult[0].stdout) !== null && _a !== void 0 ? _a : null;
        });
    }
    dotnetPackageExistsOnSystem(fullySpecifiedDotnetVersion, installType) {
        return __awaiter(this, void 0, void 0, function* () {
            let command = this.myDistroCommands(this.packageLookupCommandKey);
            const sdkPackage = yield this.myDotnetVersionPackageName(this.JsonDotnetVersion(fullySpecifiedDotnetVersion), installType);
            command = CommandExecutor_1.CommandExecutor.replaceSubstringsInCommands(command, this.missingPackageNameKey, sdkPackage);
            const commandResult = (yield this.commandRunner.executeMultipleCommands(command, null, false))[0];
            return commandResult.status === '0';
        });
    }
    getExpectedDotnetDistroFeedInstallationDirectory() {
        return this.myDistroStrings(this.expectedDistroFeedInstallDirKey);
    }
    getExpectedDotnetMicrosoftFeedInstallationDirectory() {
        return this.myDistroStrings(this.expectedMicrosoftFeedInstallDirKey);
    }
    upgradeDotnet(versionToUpgrade, installType) {
        return __awaiter(this, void 0, void 0, function* () {
            let command = this.myDistroCommands(this.updateCommandKey);
            const sdkPackage = yield this.myDotnetVersionPackageName(versionToUpgrade, installType);
            command = CommandExecutor_1.CommandExecutor.replaceSubstringsInCommands(command, this.missingPackageNameKey, sdkPackage);
            const commandResult = (yield this.commandRunner.executeMultipleCommands(command, null, false))[0].status;
            return commandResult[0];
        });
    }
    uninstallDotnet(versionToUninstall, installType) {
        return __awaiter(this, void 0, void 0, function* () {
            let command = this.myDistroCommands(this.uninstallCommandKey);
            const sdkPackage = yield this.myDotnetVersionPackageName(versionToUninstall, installType);
            command = CommandExecutor_1.CommandExecutor.replaceSubstringsInCommands(command, this.missingPackageNameKey, sdkPackage);
            const commandResult = (yield this.commandRunner.executeMultipleCommands(command, null, false))[0];
            return commandResult.status;
        });
    }
    getInstalledDotnetSDKVersions() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const command = this.myDistroCommands(this.installedSDKVersionsCommandKey);
            const commandResult = (yield this.commandRunner.executeMultipleCommands(command, {}, false))[0];
            const outputLines = commandResult.stdout.split('\n');
            const versions = [];
            for (const line of outputLines) {
                const splitLine = line.split(/\s+/);
                // list sdk lines shows in the form: version [path], so the version is the 2nd item
                if (((_a = splitLine === null || splitLine === void 0 ? void 0 : splitLine.length) !== null && _a !== void 0 ? _a : 0) === 2 && splitLine[0] !== '') {
                    versions.push(splitLine[0]);
                }
            }
            return versions;
        });
    }
    getInstalledDotnetRuntimeVersions() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const command = this.myDistroCommands(this.installedRuntimeVersionsCommandKey);
            const commandResult = (yield this.commandRunner.executeMultipleCommands(command, {}, false))[0];
            const outputLines = commandResult.stdout.split('\n');
            const versions = [];
            for (const line of outputLines) {
                const splitLine = line.split(/\s+/);
                // list runtimes lines shows in the form: runtime version [path], so the version is the 3rd item
                if (((_a = splitLine === null || splitLine === void 0 ? void 0 : splitLine.length) !== null && _a !== void 0 ? _a : 0) === 3) {
                    versions.push(splitLine[1]);
                }
            }
            return versions;
        });
    }
    getInstalledGlobalDotnetVersionIfExists() {
        return __awaiter(this, void 0, void 0, function* () {
            const command = this.myDistroCommands(this.currentInstallVersionCommandKey);
            // we need to run this command in the root directory otherwise local dotnets on the path may interfere
            const rootDir = path.parse(__dirname).root;
            const commandResult = (yield this.commandRunner.executeMultipleCommands(command, { cwd: path.resolve(rootDir), shell: true }, false))[0];
            commandResult.stdout = commandResult.stdout.replace('\n', '');
            if (!versionUtils.isValidLongFormVersionFormat(commandResult.stdout, this.context.eventStream, this.context)) {
                return null;
            }
            {
                return commandResult.stdout;
            }
        });
    }
    getDotnetVersionSupportStatus(fullySpecifiedVersion, installType) {
        return __awaiter(this, void 0, void 0, function* () {
            if (versionUtils.getFeatureBandFromVersion(fullySpecifiedVersion, this.context.eventStream, this.context) !== '1' ||
                Number(versionUtils.getMajor(fullySpecifiedVersion, this.context.eventStream, this.context)) < 6) {
                this.context.eventStream.post(new EventStreamEvents_1.DistroSupport(`Distro: Dotnet Version ${fullySpecifiedVersion} is not supported by this extension. It has a non 1 level band or < 6.0.`));
                return Promise.resolve("UNSUPPORTED" /* DotnetDistroSupportStatus.Unsupported */);
            }
            else {
                this.context.eventStream.post(new EventStreamEvents_1.DistroSupport(`Couldn't find preinstallCmdKey for ${this.distroVersion.distro} ${this.distroVersion.version} with dotnet Version ${fullySpecifiedVersion}.`));
                const availableVersions = yield this.myVersionPackages(installType, this.isMidFeedInjection);
                const simplifiedVersion = this.JsonDotnetVersion(fullySpecifiedVersion);
                for (const dotnetPackages of availableVersions) {
                    if (Number(dotnetPackages.version) === Number(simplifiedVersion)) {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        if (this.myVersionDetails().hasOwnProperty(this.preinstallCommandKey)) {
                            // If preinstall commands exist ( to add the msft feed ) then it's a microsoft feed.
                            this.context.eventStream.post(new EventStreamEvents_1.DistroSupport(`Distro: Dotnet Version ${fullySpecifiedVersion} is Microsoft support, because it has preinstallCmdKey.`));
                            return Promise.resolve("MICROSOFT" /* DotnetDistroSupportStatus.Microsoft */);
                        }
                        else {
                            this.context.eventStream.post(new EventStreamEvents_1.DistroSupport(`Version ${fullySpecifiedVersion} is Distro supported, because it has packages already.`));
                            return Promise.resolve("DISTRO" /* DotnetDistroSupportStatus.Distro */);
                        }
                    }
                }
            }
            this.context.eventStream.post(new EventStreamEvents_1.DistroSupport(`Version ${fullySpecifiedVersion} is unknown for distro ${this.distroVersion.distro} ${this.distroVersion.version} with ${this.myVersionDetails()}`));
            return Promise.resolve("UNKNOWN" /* DotnetDistroSupportStatus.Unknown */);
        });
    }
    getRecommendedDotnetVersion(installType) {
        return __awaiter(this, void 0, void 0, function* () {
            let maxVersion = '0';
            const json = yield this.myVersionPackages(installType, this.isMidFeedInjection);
            for (const dotnetPackages of json) {
                if (Number(dotnetPackages.version) > Number(maxVersion)) {
                    this.context.eventStream.post(new EventStreamEvents_1.DistroPackagesSearch(`Found version ${dotnetPackages.version} for .NET and and picking it, as it is higher than ${maxVersion}.`));
                    maxVersion = dotnetPackages.version;
                }
                this.context.eventStream.post(new EventStreamEvents_1.DistroPackagesSearch(`Skipping version ${dotnetPackages.version} for .NET and and picking it, as it is lower than ${maxVersion}.`));
            }
            if (maxVersion === '0') {
                const err = new EventStreamEvents_1.DotnetVersionResolutionError(new EventStreamEvents_1.EventBasedError('DotnetVersionResolutionError', `No packages for .NET are available.
Please refer to https://learn.microsoft.com/en-us/dotnet/core/install/linux if you'd link to install .NET.`), null);
                this.context.eventStream.post(err);
                throw (err);
            }
            // Most distros support only 100 band .NET versions, so we default to that here.
            return `${this.JsonDotnetVersion(maxVersion)}.1xx`;
        });
    }
    JsonDotnetVersion(fullySpecifiedDotnetVersion) {
        return versionUtils.getMajorMinor(fullySpecifiedDotnetVersion, this.context.eventStream, this.context);
    }
    isPackageFoundInSearch(resultOfSearchCommand, searchCommandExitCode) {
        return resultOfSearchCommand.trim() !== '' && searchCommandExitCode === '0';
    }
}
exports.GenericDistroSDKProvider = GenericDistroSDKProvider;
//# sourceMappingURL=GenericDistroSDKProvider.js.map